/** @jsx jsx */
import React, { useCallback } from 'react';
import { Stack } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form, { FormFooter, FormSection } from '@atlaskit/form';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { ApplicationLinkV3Data, ApplicationLinkV3Simple } from '../../../common/entities.tsx';
import { ApplicationUrl, DisplayName, DisplayUrl } from '../../common/connect-fields/index.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../../common/constants.tsx';
import { updateApplinkV3 } from '../../../rest/index.tsx';

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
});

type OAuth1EditForm = {
	displayName: string;
	rpcUrl: string;
	displayUrl: string;
};

type ApplinksOAuth1EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
};

const ApplinksOAuth1EditPageContents = (props: ApplinksOAuth1EditPageContentsProps) => {
	const { push } = useRouterActions();

	const onFormSubmit = useCallback(
		async (data: OAuth1EditForm) => {
			const applinkUpdate: ApplicationLinkV3Simple = {
				id: props.applink.id,
				type: props.applink.type,
				system: props.applink.system,
				primary: props.applink.primary,
				isExtraAuthRequired: props.applink.isExtraAuthRequired,

				// Only change these properties:
				name: data.displayName,
				displayUrl: data.displayUrl,
				rpcUrl: data.rpcUrl,
			};

			await updateApplinkV3(props.applink.id, applinkUpdate);

			// TODO: Also update the OAuth1 configuration
			// UI not implemented yet

			push(APPLINKS_LIST_PAGE_URL);
		},
		[props, push],
	);

	return (
		<>
			<Form<OAuth1EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title="Link details">
								<Stack space="space.200">
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl
										name="rpcUrl"
										defaultUrl={props.applink.rpcUrl}
										disabled={false}
									/>
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							<FormSection title="Connections">
								<Stack>
									<table>
										<thead>
											<tr>
												<th>Direction</th>
												<th>Local authentication</th>
												<th>Remote authentication</th>
											</tr>
										</thead>
									</table>
								</Stack>
							</FormSection>

							<FormFooter>
								<ButtonGroup label="Form submit options">
									<LinkButton href={APPLINKS_LIST_PAGE_URL} appearance="subtle">
										Cancel
									</LinkButton>
									<Button type="submit" appearance="primary" isLoading={submitting}>
										Save
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth1EditPageContents;
